import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Chip,
  Typography,
} from '@mui/material'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import { useEffect, useRef, useState } from 'react'
import CommonTable from '@/components/common/CommonTable'
import ProgressIndicator from '@/components/dashboard/ProgressIndicator'
import CompletionDatePicker from '@/components/dashboard/CompletionDatePicker'
import {
  COMPLIANCE_PAGE_SIZE,
  complianceAccordionBase,
  complianceStatusConfig,
} from '@/constants/dashboardConstant'
import {
  getDashboardComplianceRows,
  updateComplianceCompletionDate,
} from '@/api/dashboardApi'
import { useSnackbar } from '@/contexts/SnackbarProvider'

// ==================== Helpers ====================
// Builds the compliance table columns. `onCompletionSave(uuid, isoDate)` is
// invoked when a row's completion date is saved and must resolve truthy on a
// successful API persist.
const buildComplianceColumns = onCompletionSave => [
  { id: 'period', label: 'Period', width: '180px', minWidth: '120px' },
  {
    id: 'pendingWith',
    label: 'Pending with',
    width: '200px',
    minWidth: '150px',
  },
  { id: 'dueDate', label: 'Due date', width: '180px', minWidth: '120px' },
  {
    id: 'completionDate',
    label: 'Completion date',
    width: '200px',
    minWidth: '140px',
    render: (value, row) => {
      if (!value) {
        return (
          <CompletionDatePicker
            value={value}
            onSave={isoDate => onCompletionSave(row?.uuid, isoDate)}
          />
        )
      }
      return value
    },
  },
  {
    id: 'status',
    label: 'Status',
    width: '160px',
    minWidth: '120px',
    render: value => (
      <Chip
        label={value}
        size="small"
        sx={{
          fontWeight: 500,
          fontSize: 'var(--font-size-xs)',
          backgroundColor: complianceStatusConfig[value]?.bg || '#EFF6FF',
          color: 'var(--forbrand-primary)',
          height: '24px',
          borderRadius: 0,
        }}
      />
    ),
  },
]

// ==================== ComplianceCategoryAccordion ====================
// A single compliance-category accordion. Owns its row data, pagination, and
// loading state. Rows are fetched lazily on first expand (no caching — each
// pagination request goes to the network).
const ComplianceCategoryAccordion = ({
  category,
  entityTan,
  periodRange,
  defaultExpanded,
  autoLoadOnMount,
  onComplianceUpdated,
}) => {
  // ==================== State ====================
  const [rows, setRows] = useState([])
  const [totalItems, setTotalItems] = useState(0)
  const [currentPage, setCurrentPage] = useState(1)
  const [loading, setLoading] = useState(false)
  const { showSnackbar } = useSnackbar()
  // Ref guard (not state) so the initial on-expand fetch fires exactly once.
  // A ref mutates synchronously, so StrictMode's double-invoked mount effect
  // and rapid onChange events cannot both slip past the guard the way an async
  // useState update can.
  const initializedRef = useRef(false)

  // ==================== Component Helpers ====================
  // `page` here is the 1-based page the table/footer uses. The API is 0-based,
  // so we send (page - 1) as the page number in the payload.
  const buildRowsPayload = page => ({
    entityUuid: entityTan?.entityUuid,
    tanUuid: entityTan?.tanUuid,
    categoryCode: category?.categoryCode,
    fromYear: periodRange?.fromYear,
    fromMonth: periodRange?.fromMonth,
    toYear: periodRange?.toYear,
    toMonth: periodRange?.toMonth,
    page: page - 1,
    size: COMPLIANCE_PAGE_SIZE,
  })

  const loadPage = async page => {
    setLoading(true)
    setCurrentPage(page)
    try {
      const response = await getDashboardComplianceRows(buildRowsPayload(page))
      const pageData = response?.success ? response?.data?.data : null
      const content = pageData?.content || []
      const total = pageData?.totalElements ?? 0
      setRows(content)
      setTotalItems(total)
    } finally {
      setLoading(false)
    }
  }

  // ==================== Effects ====================
  useEffect(() => {
    // The first category of an entity/TAN is default-expanded, so it never
    // fires its own onChange. It loads here once its entity is opened:
    // immediately for the first entity (open on mount), or when the user opens
    // a collapsed entity. Guarded so it fetches exactly once. On initial load
    // only the first entity's first category qualifies -> a single rows call.
    if (autoLoadOnMount && !initializedRef.current) {
      initializedRef.current = true
      loadPage(1)
    }
  }, [autoLoadOnMount])

  // ==================== Handlers ====================
  const handleAccordionChange = (_event, expanded) => {
    // Fetch the first page only once, the first time this category is opened.
    if (expanded && !initializedRef.current) {
      initializedRef.current = true
      loadPage(1)
    }
  }

  const handlePageChange = page => {
    loadPage(page)
  }

  // Persists a row's completion date. Returns true on success so the picker can
  // commit locally; on success we also patch the row's completionDate in state.
  const handleCompletionSave = async (uuid, isoDate) => {
    if (!uuid) return false
    const response = await updateComplianceCompletionDate(uuid, isoDate)
    if (response?.success && response?.data?.success) {
      showSnackbar(
        response?.data?.data ||
          'Compliance completion date updated successfully',
        'success'
      )
      // Refetch this accordion's rows so the table reflects the backend state
      // (updated completion date, status, totals) rather than a local patch.
      // Stays on the page the user is viewing.
      loadPage(currentPage)
      // Refresh the parent compliance tree so country/entity/category progress
      // counts and totals reflect the saved completion date.
      onComplianceUpdated?.()
      return true
    } else {
      const errorMessage =
        response?.data?.errors[0]?.message ||
        'Failed to update compliance completion date'
      showSnackbar(errorMessage, 'error')
    }
    return false
  }

  // ==================== Derived Data ====================
  // Rebuilt each render so the save handler always closes over the current
  // page (keeps the user on their page after a completion-date save).
  const columns = buildComplianceColumns(handleCompletionSave)

  // ==================== Return ====================
  return (
    <Accordion
      defaultExpanded={defaultExpanded}
      onChange={handleAccordionChange}
      disableGutters
      sx={{
        ...complianceAccordionBase,
        border: '1px solid var(--border-subtel)',
        backgroundColor: 'var(--white-color)',
        mx: 2,
        mb: 1,
        '&:first-of-type': { mt: 1, borderRadius: 0 },
        '&:first-of-type.Mui-expanded': { mt: 1 },
        '&.Mui-expanded': { mb: 1 },
        '&:last-child': { mb: 1 },
        '&:last-child.Mui-expanded': { mb: 1 },
      }}
    >
      <AccordionSummary
        expandIcon={
          <ExpandMoreIcon sx={{ color: 'var(--forbrand-secondary)' }} />
        }
        sx={{
          px: 2,
          minHeight: 48,
          '& .MuiAccordionSummary-content': {
            alignItems: 'center',
            justifyContent: 'space-between',
          },
        }}
      >
        <Typography
          sx={{
            fontSize: 'var(--font-size-md)',
            fontWeight: 500,
            color: 'var(--forbrand-primary)',
          }}
        >
          {category.categoryName}
        </Typography>
        <ProgressIndicator
          completed={category.completed}
          total={category.total}
        />
      </AccordionSummary>

      <AccordionDetails
        sx={{ p: 0, '& .MuiTableContainer-root': { marginBottom: 0 } }}
      >
        <CommonTable
          columns={columns}
          data={rows}
          loading={loading}
          showActions={false}
          showCheckbox={false}
          showFooter={true}
          stickyFooter={false}
          totalItems={totalItems}
          currentPage={currentPage}
          itemsPerPage={COMPLIANCE_PAGE_SIZE}
          showItemsPerPage={false}
          onPageChange={handlePageChange}
          applyMinHeightConstraints={false}
          emptyMessage="No compliance data available"
        />
      </AccordionDetails>
    </Accordion>
  )
}

export default ComplianceCategoryAccordion
